import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["priceInput", "suggestion", "noteCheckbox", "noteText", "noteSection", "sourceSelect", "sourceDescription"]
  static values = {
    latestPrice: Number,      // Latest mark price in cents, passed from view
    latestMarkDate: String    // Latest mark date as ISO8601 string
  }

  static sourceDescriptions = {
    LATEST_BUYER: "Use when you have a new round price from the latest investor. This represents the price per share paid in the most recent funding round, and is the most common way to mark equity positions.",
    OTHER: "Use for periodic fair market value adjustments, secondary market pricing, 409A valuations, comparable company analysis, or any other valuation methodology."
  }

  connect() {
    this.lastSuggestedNote = null
    this.checkPriceChange()
    this.updateSourceDescription()
  }

  checkPriceChange() {
    const newPriceDollars = parseFloat(this.priceInputTarget.value) || 0
    const newPriceCents = Math.round(newPriceDollars * 100)
    const latestPriceCents = this.latestPriceValue

    if (latestPriceCents === 0 || newPriceCents === 0) {
      this.hideSuggestion()
      return
    }

    const percentChange = ((newPriceCents - latestPriceCents) / latestPriceCents) * 100
    const absChange = Math.abs(percentChange).toFixed(0)

    // Only show suggestion if change is significant (>10%)
    if (Math.abs(percentChange) < 10) {
      this.hideSuggestion()
      return
    }

    // Calculate time period and annualized return
    const timeContext = this.calculateTimeContext(newPriceCents, latestPriceCents)
    const oldPrice = (latestPriceCents / 100).toFixed(2)
    const newPrice = newPriceDollars.toFixed(2)

    if (percentChange < 0) {
      const message = timeContext.showAnnualized
        ? `This is a down round (-${absChange}% total, ${timeContext.annualizedStr} annualized over ${timeContext.periodStr}). Consider noting this.`
        : `This is a down round (-${absChange}%). Consider noting this.`

      const noteText = timeContext.showAnnualized
        ? `Down round: valuation decreased from $${oldPrice} to $${newPrice} per share (-${absChange}% total, ${timeContext.annualizedStr} annualized over ${timeContext.periodStr}).`
        : `Down round: valuation decreased from $${oldPrice} to $${newPrice} per share (-${absChange}%).`

      this.showSuggestion(message, noteText, "down")
    } else {
      const message = timeContext.showAnnualized
        ? `This is an up round (+${absChange}% total, ${timeContext.annualizedStr} annualized over ${timeContext.periodStr}). Consider noting this.`
        : `This is an up round (+${absChange}%). Consider noting this.`

      const noteText = timeContext.showAnnualized
        ? `Up round: valuation increased from $${oldPrice} to $${newPrice} per share (+${absChange}% total, ${timeContext.annualizedStr} annualized over ${timeContext.periodStr}).`
        : `Up round: valuation increased from $${oldPrice} to $${newPrice} per share (+${absChange}%).`

      this.showSuggestion(message, noteText, "up")
    }
  }

  updateSourceDescription() {
    if (!this.hasSourceSelectTarget || !this.hasSourceDescriptionTarget) return

    const value = this.sourceSelectTarget.value
    const description = this.constructor.sourceDescriptions[value]

    if (description) {
      this.sourceDescriptionTarget.textContent = description
      this.sourceDescriptionTarget.classList.remove("hidden")
    } else {
      this.sourceDescriptionTarget.textContent = ""
      this.sourceDescriptionTarget.classList.add("hidden")
    }
  }

  calculateTimeContext(newPriceCents, oldPriceCents) {
    // Default: don't show annualized if we don't have a date or period is short
    const result = { showAnnualized: false, annualizedStr: "", periodStr: "" }

    if (!this.latestMarkDateValue) {
      return result
    }

    const latestDate = new Date(this.latestMarkDateValue)
    const today = new Date()

    // Calculate years (as a decimal)
    const msPerYear = 365.25 * 24 * 60 * 60 * 1000
    const years = (today - latestDate) / msPerYear

    // Only show annualized for periods >= 1 year
    if (years < 1) {
      return result
    }

    // Calculate annualized return using CAGR formula: (endValue/startValue)^(1/years) - 1
    const ratio = newPriceCents / oldPriceCents
    const annualizedReturn = (Math.pow(ratio, 1 / years) - 1) * 100

    // Format the period string
    let periodStr
    if (years >= 2) {
      periodStr = `${years.toFixed(1)} years`
    } else {
      periodStr = `${years.toFixed(1)} year`
    }

    // Format annualized return
    const sign = annualizedReturn >= 0 ? "+" : ""
    const annualizedStr = `${sign}${annualizedReturn.toFixed(0)}%`

    return {
      showAnnualized: true,
      annualizedStr,
      periodStr
    }
  }

  showSuggestion(message, suggestedNote, direction) {
    if (this.hasSuggestionTarget) {
      this.suggestionTarget.textContent = message
      this.suggestionTarget.classList.remove("hidden")

      // Add appropriate styling based on direction
      this.suggestionTarget.classList.remove("text-red-600", "text-green-600", "dark:text-red-400", "dark:text-green-400")
      if (direction === "down") {
        this.suggestionTarget.classList.add("text-red-600", "dark:text-red-400")
      } else {
        this.suggestionTarget.classList.add("text-green-600", "dark:text-green-400")
      }
    }

    // Auto-check the "add note" checkbox
    if (this.hasNoteCheckboxTarget && !this.noteCheckboxTarget.checked) {
      this.noteCheckboxTarget.checked = true
      this.noteCheckboxTarget.dispatchEvent(new Event("change"))
    }

    // Pre-fill note text if empty OR if it matches a previous auto-generated suggestion
    // This allows updating the suggestion when price changes, but won't overwrite user edits
    if (this.hasNoteTextTarget) {
      const currentValue = this.noteTextTarget.value
      const isAutoGenerated = !currentValue || currentValue === this.lastSuggestedNote

      if (isAutoGenerated) {
        this.noteTextTarget.value = suggestedNote
        this.lastSuggestedNote = suggestedNote
      }
    }
  }

  hideSuggestion() {
    if (this.hasSuggestionTarget) {
      this.suggestionTarget.textContent = ""
      this.suggestionTarget.classList.add("hidden")
    }
  }

  toggleNoteSection() {
    if (this.hasNoteSectionTarget) {
      if (this.noteCheckboxTarget.checked) {
        this.noteSectionTarget.classList.remove("hidden")
      } else {
        this.noteSectionTarget.classList.add("hidden")
      }
    }
  }
}
